import { ReactElement } from 'react';
import { AutocompleteInput, FilterPayload, ReferenceInput, required } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { SoftwareComponent } from 'types';

export interface SoftwareVersionInputProps {
  component: SoftwareComponent;
  /**
   * Pre-selected version, applied as the input's own default value. Only the create form uses
   * it (a clone carries its source release's selection); the edit form seeds the whole
   * selection through the form's `defaultValues` instead, so that clearing it on a hardware
   * change cannot be undone by an input re-applying its default.
   */
  defaultValue?: number;
  /**
   * Form field this input writes to. Required, and deliberately without a default: passing the
   * component's own name (`tover.tafel`) makes react-hook-form read it as a *path* and nest the
   * value under a `tover` object, which only `SoftwareReleaseCreate` wants — its toolbar reads
   * `tover[name.split('.')[1]]` back out. Making every caller say so keeps that coupling visible
   * at both ends instead of being what a new caller silently gets.
   */
  source: string;
  /** A string is translated by react-admin; wrap a literal in a fragment to render it as-is. */
  label?: string | ReactElement | false;
  /**
   * Whether a component the hardware marks as required must carry a version before the form can
   * be submitted. The create form always enforces it — a new release has to be complete. The edit
   * form turns it off for a release that carries no versions at all, which the API deliberately
   * leaves renameable: a client-side `required` would be the only thing standing between such a
   * release and a rename.
   */
  enforceRequired?: boolean;
}

/**
 * Picks the version of one software component for a release.
 *
 * The choices follow the release being edited rather than the component alone: they are scoped
 * to the selected hardware, and restricted to tagged versions unless the release is custom — a
 * branch version on a non-custom release is rejected by the API.
 *
 * Shared by `SoftwareReleaseCreate` and `SoftwareReleaseEdit` (TVR-1909).
 */
export const SoftwareVersionInput = ({
  component,
  defaultValue,
  source,
  label,
  enforceRequired = true,
}: SoftwareVersionInputProps) => {
  const isCustom = useWatch({ name: 'custom' });
  const id: number = useWatch({ name: 'hardware_id' });
  const isRequired = enforceRequired && component.required_hardware_ids?.includes(id);

  const filter: FilterPayload = { hardware_id: id, software_component_id: component.id };

  if (!isCustom) {
    filter.type = 'tag';
  }

  return (
    <ReferenceInput source={source} reference="software_versions" filter={filter}>
      <AutocompleteInput
        label={label}
        validate={isRequired ? required() : undefined}
        optionText="name"
        defaultValue={defaultValue}
      />
    </ReferenceInput>
  );
};
